"use client";
import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import CustomInput from "@/components/shared/CustomInput";
import Image from "next/image";
import searchIcon from "@/public/images/search.svg";
import { Separator } from "@/components/ui/separator";
import Pagination from "@/components/shared/Pagination";
import { SelectLoggedUser } from "@/redux/Auth";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import TelegramLogin from "@/components/HomeComponents/TelegramLogin";
import AdPannerFooter from "@/components/shared/AdPannerFooter/AdPannerFooter";
import instructorImage from "@/public/images/instructorImage.svg";
import category1 from "@/public/images/eduegoo.jpeg";
import category2 from "@/public/images/galaxyStar.jpeg";
import category3 from "@/public/images/externalBooks.jpg";
import BookCard from "@/components/shared/BookCard";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import USER, { SelectBooks } from "@/redux/User";
import GENERAL, { SelectSchoolYears } from "@/redux/GENERAL";
import SocialMedia from "@/components/shared/socialMedia/SocialMedia";
import PaginationControls from "@/components/shared/PaginationControls";

// Category data - you can move this to Redux or fetch from API
const categories = [
  {
    id: 1,
    name: "مكتبة Eduegoo",
    image: category1,
  },
];

const Page = () => {
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [schoolYearSelected, setSchoolYearSelected] = useState<any>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const books = useAppSelector(SelectBooks);
  const schoolYear = useAppSelector(SelectSchoolYears);
  const loggedUser = useAppSelector(SelectLoggedUser);

  useEffect(() => {
    const fetchSchoolYears = async () => {
      const params =
        loggedUser?.isLoggedIn && loggedUser?.userToken
          ? {
              mainEducationType:
                loggedUser?.userData?.details?.type_education?.id,
            }
          : {};

      await dispatch(GENERAL.thunks.doGetSchoolYears(params));
    };

    fetchSchoolYears();
  }, [dispatch, loggedUser]);

  // Only fetch books when a category is selected
  useEffect(() => {
    if (selectedCategory !== null) {
      const requestParams = {
        search: searchValue,
        subject_id: "",
        school_years: schoolYearSelected,
        education_types: [],
        teacher_name: "",
        page: currentPage,
        // Try different parameter names - adjust based on your API
        category_id: selectedCategory,
        categoryId: selectedCategory,
        category: selectedCategory,
      };

      console.log("Sending API request with params:", requestParams);
      console.log("Selected category ID:", selectedCategory);

      dispatch(USER.thunks.doGetBooks(requestParams))
        .then((res) => {
          console.log("API Response:", res);
          setTotalPages(res.payload.data.total_pages);
        })
        .catch((error) => {
          console.error("API Error:", error);
        });
    }
  }, [
    searchValue,
    schoolYearSelected,
    selectedCategory,
    currentPage,
    dispatch,
  ]);

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected + 1);
  };

  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
    setCurrentPage(1); // Reset to first page when category changes
    // Reset other filters when changing category
    setSearchValue("");
    setSchoolYearSelected([]);
  };

  return (
    <div>
      <Header />

      <div className="container my-10 flex flex-col gap-10">
        {/* Category Selection Section - Centered */}
        <div className="bg-gradient-to-br from-[#232c37] to-[#2a2d30] rounded-3xl p-8 shadow-2xl">
          <h3 className="text-white text-2xl font-bold mb-8 text-center tracking-wide">
            اختر المكتبة
          </h3>

          <div className="flex justify-center mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl w-full">
              {categories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  className={`
            group cursor-pointer rounded-2xl p-6 transition-all duration-500 border-2 relative overflow-hidden backdrop-blur-sm
            ${
              selectedCategory === category.id
                ? "bg-gradient-to-br from-primaryColor to-primaryColor/90 text-black border-primaryColor shadow-2xl shadow-primaryColor/25 scale-105"
                : "bg-gradient-to-br from-[#333e4b]/80 to-[#3d4043]/80 text-white border-[#5a5d60] hover:border-primaryColor/60 hover:scale-[1.02] hover:shadow-xl hover:from-[#525559]/90 hover:to-[#454851]/90"
            }
          `}
                >
                  <div className="flex items-center justify-center flex-col gap-5">
                    {/* Category Image Container */}
                    <div className="relative">
                      <div
                        className={`
                w-24 h-24 rounded-2xl overflow-hidden shadow-lg transition-all duration-300
                ${
                  selectedCategory === category.id
                    ? "bg-white/20 ring-4 ring-white/30"
                    : "bg-gray-100 group-hover:shadow-xl group-hover:scale-105"
                }
              `}
                      >
                        <Image
                          src={category.image}
                          alt={category.name}
                          width={96}
                          height={96}
                          className="w-full h-full transition-transform duration-300 group-hover:scale-110"
                          onError={(e) => {
                            e.currentTarget.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 19.5A2.5 2.5 0 0 1 6.5 17H20'/%3E%3Cpath d='M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z'/%3E%3C/svg%3E";
                          }}
                        />
                      </div>

                      {/* Selected indicator */}
                      {selectedCategory === category.id && (
                        <div className="absolute -top-2 -right-2 bg-[#232c37] text-primaryColor rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold shadow-lg">
                          ✓
                        </div>
                      )}
                    </div>

                    {/* Category Text */}
                    <div className="text-center space-y-2">
                      <h4
                        className={`text-lg font-semibold transition-colors duration-300 ${
                          selectedCategory === category.id
                            ? "text-black"
                            : "text-white group-hover:text-primaryColor"
                        }`}
                      >
                        {category.name}
                      </h4>
                      <div
                        className={`text-sm transition-all duration-300 ${
                          selectedCategory === category.id
                            ? "text-black/70 font-medium"
                            : "text-gray-300 group-hover:text-gray-200"
                        }`}
                      >
                        انقر للتصفية حسب هذه المكتبة
                      </div>
                    </div>
                  </div>

                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none" />
                </div>
              ))}
            </div>
          </div>

          {/* Clear Category Filter Button */}
          {selectedCategory !== null && (
            <div className="text-center">
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setCurrentPage(1);
                }}
                className="group bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-8 py-3 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-2 mx-auto"
              >
                <svg
                  className="w-4 h-4 transition-transform group-hover:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                إزالة تصفية المكتبة
              </button>
            </div>
          )}
        </div>

        {/* Search and School Year Filters */}
        {selectedCategory !== null && (
          <div className="bg-[#232c37] rounded-[20px] p-3 flex flex-col gap-5">
            <div className="flex items-center bg-[#333e4b] rounded-[5px] py-2 pl-3">
              <Formik
                initialValues={{ search: "" }}
                onSubmit={(values) => {
                  setSearchValue(values.search);
                  setCurrentPage(1);
                }}
              >
                {() => (
                  <Form className="w-full flex justify-between gap-4">
                    <CustomInput
                      type="text"
                      name="search"
                      placeholder="بحث عن كتاب في المنصة"
                      fullWidth={true}
                    />
                    <button type="submit">
                      <Image src={searchIcon} alt="search" />
                    </button>
                  </Form>
                )}
              </Formik>
            </div>

            {/* School Year Filter */}
            <div className="flex gap-5 flex-wrap">
              {schoolYear.map((year: any, index: number) => (
                <button
                  key={index}
                  onClick={() => {
                    setSchoolYearSelected((prev: any) =>
                      prev.includes(year.id)
                        ? prev.filter((item: any) => item !== year.id)
                        : [...prev, year.id]
                    );
                    setCurrentPage(1);
                  }}
                  className={`${
                    schoolYearSelected.includes(year.id)
                      ? "bg-primaryColor text-black"
                      : "bg-[#333e4b] text-white"
                  } rounded-[5px] text-14 w-fit py-3 px-5`}
                >
                  {year.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Active Filters Display - Only show when category is selected */}
        {selectedCategory !== null &&
          (selectedCategory !== null ||
            schoolYearSelected.length > 0 ||
            searchValue) && (
            <div className="bg-[#2a2d30] rounded-[10px] p-3">
              <h4 className="text-white text-sm mb-2">الفلاتر النشطة:</h4>
              <div className="flex flex-wrap gap-2">
                {selectedCategory !== null && (
                  <span className="bg-primaryColor text-black px-3 py-1 rounded-[5px] text-sm">
                    الفئة:{" "}
                    {
                      categories.find((cat) => cat.id === selectedCategory)
                        ?.name
                    }
                  </span>
                )}
                {searchValue && (
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-[5px] text-sm">
                    البحث: {searchValue}
                  </span>
                )}
                {schoolYearSelected.length > 0 && (
                  <span className="bg-green-500 text-white px-3 py-1 rounded-[5px] text-sm">
                    السنوات الدراسية: {schoolYearSelected.length}
                  </span>
                )}
              </div>
            </div>
          )}

        {/* Books Grid - Only show when category is selected */}
        {selectedCategory !== null ? (
          books.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {books.map((item: any, index: number) => (
                <BookCard
                  key={index}
                  id={item.id}
                  image={item.image}
                  years={item.school_years}
                  teacherName={item.teacher_name}
                  teacherImage={item.teacher_image || instructorImage}
                  subject={item.subject.name}
                  price={item.price}
                  name={item.name}
                  description={item.description}
                  examNumber={item.quantity}
                  starNumber={item.stars}
                  fileNumber={item.pages}
                />
              ))}
            </div>
          ) : (
            <div className="text-center text-red-400 bg-[#333e4b] rounded-[5px] py-5 px-3">
              لم يتم العثور على بيانات بناءً على الفلاتر الخاصة بك
            </div>
          )
        ) : (
          // Show message when no category is selected
          <div className="text-center text-white bg-[#333e4b] rounded-[15px] py-10 px-5">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold mb-2">
              اختر مكتبة لعرض الكتب
            </h3>
            <p className="text-gray-300">
              يرجى اختيار إحدى المكتبات أعلاه لعرض الكتب المتاحة
            </p>
          </div>
        )}

        {/* Pagination - Only show when category is selected and books exist */}
        {selectedCategory !== null && books.length > 0 && (
          <PaginationControls
            currentPage={currentPage}
            totalQuestions={totalPages * 10}
            questionsPerPage={10}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <SocialMedia />
      <Footer />
      {/* <TelegramLogin /> */}
      {/* <AdPannerFooter /> */}
    </div>
  );
};

export default Page;
